import type { ReactNode } from "react";

import { cx } from "@/lib/cx";

/**
 * Icons used only by the tasks screen.
 *
 * They are drawn on the **same 20×20 grid at the same 1.6px round-capped
 * stroke** as `ui/overlay/icons`, because the two families now sit next to each
 * other constantly: a contextual menu holds `Move to tomorrow` (from here) two
 * rows above `Delete` (from there), and a 24-grid icon at 1.75px stroke beside
 * a 20-grid one at 1.6px reads as two different icon sets in one menu. Size is
 * set by the caller with `size-*`, defaulting to 16px so a glyph lines up with
 * `text-body` without a vertical nudge — the same contract as the shared set.
 *
 * Anything a second surface also needs — chevrons, the calendar, ✕, ⋯, the
 * pencil, the bin, the image marker — belongs in `@/ui`'s glyph set instead and
 * is imported from there. What is left here is genuinely only ours: the reorder
 * grip, the two reschedule arrows, the capture field's send arrow, and the two
 * empty-state marks.
 */

type IconProps = { className?: string };

function Stroke({ className, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cx("size-4 shrink-0", className)}
    >
      {children}
    </svg>
  );
}

/**
 * The reorder grip.
 *
 * Three horizontal rules, not a six-dot waffle. iOS has exactly one "drag me to
 * reorder" mark and this is it; the dot grid is the web's, and next to an
 * otherwise iOS row it was the loudest thing in the gutter.
 */
export function ReorderIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M5 6.5h10M5 10h10M5 13.5h10" />
    </Stroke>
  );
}

/** Push this task forward — to tomorrow. */
export function ArrowRightIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M3.5 10h13M11.5 5.2 16.3 10l-4.8 4.8" />
    </Stroke>
  );
}

/** Pull this task back — to today. */
export function ArrowLeftIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M16.5 10h-13M8.5 5.2 3.7 10l4.8 4.8" />
    </Stroke>
  );
}

/**
 * Commit the capture.
 *
 * Up rather than right: the field sends the title *into* the list above the
 * fold, and ⬆ inside a filled circle is the gesture iOS uses everywhere a short
 * string is submitted from a single-line field.
 */
export function ArrowUpIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M10 16.5v-13M5.2 8.5 10 3.7l4.8 4.8" />
    </Stroke>
  );
}

/** The empty state for "this filter matches nothing". */
export function TagIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M9.6 2.9H4.5a1.6 1.6 0 0 0-1.6 1.6v5.1c0 .42.17.83.47 1.13l6 6a1.6 1.6 0 0 0 2.26 0l5.1-5.1a1.6 1.6 0 0 0 0-2.26l-6-6a1.6 1.6 0 0 0-1.13-.47Z" />
      <circle cx="6.6" cy="6.6" r="1" />
    </Stroke>
  );
}

/** The empty state for "there is nothing here yet". */
export function EmptyInboxIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M2.9 11.4 4.6 4.2h10.8l1.7 7.2v4.6H2.9v-4.6Z" />
      <path d="M2.9 11.4h3.4l.9 2h5.6l.9-2h3.4" />
    </Stroke>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * The priority mark: a 6px filled disc, or a hollow ring for "no priority".
 *
 * Six pixels, not a pill, for the reason `kanban/lanes.ts` gives about cards
 * and which holds harder on a 40px checklist row: a fixed-position dot down a
 * column of thirty rows is scannable in one eye movement, and the row has no
 * horizontal budget left after the tick, the number, the title, the badge, the
 * reschedule and the ⋯.
 *
 * Colour is never the only carrier. The dot is drawn beside a `title` and an
 * `sr-only` label on the row, and the sort order says the same thing again by
 * position, so a red/yellow pair that some people cannot tell apart is the
 * third statement of a fact, not the first.
 *
 * The hollow variant exists only for the menu, where "No priority" needs a
 * glyph in the same slot as the two colours so the three items line up.
 */
export function PriorityDotIcon({
  tone,
  className,
}: {
  tone: "high" | "medium" | "none";
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        "inline-block size-1.5 shrink-0 rounded-full",
        tone === "high" && "bg-danger",
        // Amber. It is the workout/streak colour elsewhere in the app but
        // appears nowhere on this screen, and the carried-over badge already
        // avoids it in favour of coral — so there is nothing here for it to be
        // confused with.
        tone === "medium" && "bg-streak",
        tone === "none" && "border border-line",
        className,
      )}
    />
  );
}

/** The shortlist mark. Filled when the task is on it, outlined when it is not. */
export function PinIcon({ className, filled = false }: IconProps & { filled?: boolean }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cx("size-4 shrink-0", className)}
    >
      {/* A drawing pin seen head-on: the pressed head, the shaft, the point. */}
      <path d="M12.4 2.9 17.1 7.6l-2.1 1a2 2 0 0 0-.9.7l-2 2.8-4.2-4.2 2.8-2a2 2 0 0 0 .7-.9l1-2.1Z" />
      <path d="m7.9 12.1-4 5 5-4" fill="none" />
    </svg>
  );
}
